/*
 * A clock that is hard to fake.
 *
 * `Date.now()` is whatever the machine says, and the machine belongs to the visitor. This reads
 * the real functions once at startup, counts forward with `performance.now()` - which ignores
 * changes to the machine clock - and asks our server for the time once, to catch a clock that
 * was already wrong when the page opened.
 *
 * It makes the easy bypasses harder, nothing more. What matters is still checked server-side.
 */

// A bigger gap than 2 seconds means the time was moved, rather than the usual drift.
const TOLERANCE_MS = 2 * 1000;

const readNativePerformanceNow = (): (() => number) | null => {
    try {
        if (typeof performance === 'undefined' || typeof performance.now !== 'function') {
            return null;
        }

        return performance.now.bind(performance);
    } catch {
        return null;
    }
};

const readNativePerformanceTimeOrigin = (): number | null => {
    try {
        const { timeOrigin } = performance;

        return typeof timeOrigin === 'number' && Number.isFinite(timeOrigin) ? timeOrigin : null;
    } catch {
        return null;
    }
};

class TrustedTime {
    private readonly nativeDateNow: () => number = Date.now;
    private readonly nativeDateParse: (value: string) => number = Date.parse;
    private readonly nativePerformanceNow = readNativePerformanceNow();
    private readonly nativePerformanceTimeOrigin = readNativePerformanceTimeOrigin();

    private readonly listeners = new Set<() => void>();

    private baselineWallClock = this.nativeDateNow();
    private baselineElapsed = this.nativePerformanceNow?.() ?? null;

    constructor() {
        // On the server there is nobody to fool, and this instance is shared by every request at
        // once, so it reads the clock and nothing more.
        if (typeof window === 'undefined') return;

        this.crossCheckBaseline();
        this.askServerForTime();
    }

    /** Current time in ms. */
    now(): number {
        if (this.nativePerformanceNow && this.baselineElapsed !== null) {
            return this.baselineWallClock + (this.nativePerformanceNow() - this.baselineElapsed);
        }

        return this.nativeDateNow();
    }

    /** Tells the caller when the clock is reset, so anything timed against it can be redone. */
    subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    };

    /** The time according to the Performance API, which never goes through `Date`. */
    private readPerformanceWallClock(): number | null {
        if (this.nativePerformanceTimeOrigin === null || !this.nativePerformanceNow) return null;

        return this.nativePerformanceTimeOrigin + this.nativePerformanceNow();
    }

    private crossCheckBaseline(): void {
        const independentWallClock = this.readPerformanceWallClock();

        if (
            independentWallClock === null ||
            Math.abs(independentWallClock - this.baselineWallClock) <= TOLERANCE_MS
        ) {
            return;
        }

        // A clock we did not get from `Date` disagrees with the one we did. Believe it.
        this.resetBaseline(independentWallClock);
    }

    /**
     * A HEAD on the page we are already on, so there is no extra route and no body to download.
     * We read the `Date` header rather than a response body because our CDN keeps the header
     * current even on a cached page, where a body would hand back a stale time.
     */
    private askServerForTime(): void {
        fetch(window.location.href, { method: 'HEAD', cache: 'no-store', credentials: 'omit' })
            .then((response) => this.applyServerDate(response.headers.get('date')))
            // Keep the local clock. A failed request must not stop a countdown working.
            .catch(() => undefined);
    }

    /** Read with the captured `Date.parse`, so a replaced `Date` cannot rewrite the answer. */
    private applyServerDate(dateHeader: string | null): void {
        if (dateHeader === null) return;

        const serverTimeMs = this.nativeDateParse(dateHeader);

        if (!Number.isFinite(serverTimeMs)) return;
        if (Math.abs(serverTimeMs - this.now()) <= TOLERANCE_MS) return;

        this.resetBaseline(serverTimeMs);
        this.listeners.forEach((listener) => listener());
    }

    private resetBaseline(wallClock: number): void {
        this.baselineWallClock = wallClock;
        this.baselineElapsed = this.nativePerformanceNow?.() ?? null;
    }
}

/*
 * One instance for the whole app, built when this module is first imported - so a page with no
 * use for the time never builds it and never makes the request.
 */
export const trustedTime = new TrustedTime();
